import { CompanyPeriodRequestStatus } from '../schemas/CompanyPeriodRequest.schema';
import { createTemplateAssignment, TemplateAssignment } from '../schemas/template-assignment-schema';
import { colors } from '../theme/colors';
import { KpiPeriod, KpiSection } from './company-financials.data-model';
import { IKPITemplate } from './kpi-template.data-model';

export const KPIRequestStatus = {
  Archived: 'archived',
  NotSent: 'notSent',
  Sent: 'sent',
  Submitted: 'submitted',
  Accepted: 'accepted',
  Rejected: 'rejected',

  ManagedServiceInProgress: 'managedServiceInProgress', // ms started the review process
  ManagedServiceSubmitted: 'managedServiceSubmitted', // portco submitted, waiting for ms to start review
  ManagedServiceFinished: 'managedServiceFinished', // ms finished review
} as const;
export type KPIRequestStatus = (typeof KPIRequestStatus)[keyof typeof KPIRequestStatus];

export function isKPIPendingSubmission(status: KPIRequestStatus | string): boolean {
  return status === KPIRequestStatus.Rejected || status === KPIRequestStatus.Sent;
}

// FIXME: https://foresightdata.atlassian.net/browse/MAGGIE-7630
export enum KPIRequestPeriod {
  Month = 'month',
  Quarter = 'quarter',
  Annual = 'annual',
  None = 'none',
}

export enum KPIRequestFrequency {
  Monthly = 'monthly',
  Quarterly = 'quarterly',
  Annual = 'annually',
  None = 'none',
}

export const KpiPeriodToKpiRequestPeriod: Record<KpiPeriod, KPIRequestPeriod> = {
  [KpiPeriod.month]: KPIRequestPeriod.Month,
  [KpiPeriod.quarter]: KPIRequestPeriod.Quarter,
  [KpiPeriod.year]: KPIRequestPeriod.Annual,
};

export const kpiRequestPeriodToKpiPeriod: Record<KPIRequestPeriod, KpiPeriod | null> = {
  [KPIRequestPeriod.Month]: KpiPeriod.month,
  [KPIRequestPeriod.Quarter]: KpiPeriod.quarter,
  [KPIRequestPeriod.Annual]: KpiPeriod.year,
  [KPIRequestPeriod.None]: null, // TODO ????
};

export const periodToFrequency: Record<KpiPeriod, KPIRequestFrequency> = {
  [KpiPeriod.month]: KPIRequestFrequency.Monthly,
  [KpiPeriod.quarter]: KPIRequestFrequency.Quarterly,
  [KpiPeriod.year]: KPIRequestFrequency.Annual,
};

export const frequencyToPeriod: Record<KPIRequestFrequency, KPIRequestPeriod> = {
  [KPIRequestFrequency.Quarterly]: KPIRequestPeriod.Quarter,
  [KPIRequestFrequency.Annual]: KPIRequestPeriod.Quarter,
  [KPIRequestFrequency.Monthly]: KPIRequestPeriod.Month,
  [KPIRequestFrequency.None]: KPIRequestPeriod.None,
};

export type KPIRequestResponse = IKPIRequestDataModel | null;

export enum ReminderType {
  manual = 'Additional',
  scheduled = 'Due date',
}
export interface IKPIRequestReminderDataModel {
  id: number;
  createdAt: string;
  updatedAt: string;
  kpiRequestId: number;
  type: keyof typeof ReminderType;
  sentAt: string;
}

export interface IKPIRequestBulkSendResponse {
  requestIds: number[];
  message: string;
}

export interface IKPIRequestDataModel extends Omit<TemplateAssignment, 'fye'> {
  acceptMessage?: string;
  archivedAt: string | null;
  companyId: number;
  deletedAt?: string | null;
  dueDate?: string | null;
  fye: string;
  kpiRequestReminders: IKPIRequestReminderDataModel[];
  kpiRequestResponse?: IKPIRequestResponse | null;
  period: string;
  rejectReason?: string;
  sentAt: string | null;
  status: KPIRequestStatus;
  templateId: number;
}

export function createKpiRequestReminderDataModel(
  overrides: Partial<IKPIRequestReminderDataModel> = {}
): IKPIRequestReminderDataModel {
  return {
    id: 0,
    createdAt: '',
    updatedAt: '',
    kpiRequestId: 0,
    type: 'scheduled',
    sentAt: '',
    ...overrides,
  };
}

export interface IKPIRequestForm extends Pick<
  IKPIRequestDataModel,
  'templateUuid' | 'frequency' | 'respondent' | 'reminderThreshold'
> {
  fye: string;
}

export const createKPIRequestDataModel = (
  overrides: Partial<IKPIRequestDataModel> = {}
): IKPIRequestDataModel => ({
  ...createTemplateAssignment(),
  archivedAt: null,
  companyId: 1,
  frequency: KPIRequestFrequency.Quarterly,
  fye: '2021-12-31',
  id: 1,
  kpiRequestReminders: [],
  period: '',
  respondent: [],
  sentAt: null,
  status: KPIRequestStatus.NotSent,
  templateId: 0,
  templateUuid: '',
  ...overrides,
});

export interface IKPIResponseFormSectionData {
  sectionId: number;
  value: unknown;
}

export interface IKPIDataValue {
  kpiId: number;
  value: unknown;
  date: string;
  section: KpiSection;
  numberFormatValue?: string;
}

export function createKpiDataValue(overrides: Partial<IKPIDataValue> = {}): IKPIDataValue {
  return {
    kpiId: 0,
    value: 0,
    date: '',
    section: KpiSection.actual,
    ...overrides,
  };
}

export interface IKPIData {
  period: KPIRequestPeriod;
  currencyCode: string;
}

export interface IKPIResponseFormKPIData extends IKPIData {
  values: Array<IKPIDataValue[]>;
}

export interface IKPIResponseRequestKPIData extends IKPIData {
  values: IKPIDataValue[];
}

export type KPIFileUploadedType = {
  companyId?: number;
  fileId?: string;
  fileName: string;
  contentType: string;
  fileSize?: number;
};

export interface IKPIResponseForm {
  id?: number;
  lastFocusedMetric: {
    metricParentRowIdx: number;
    metricCeilColIdx: number;
    focused: boolean;
  };
  requestId?: number;
  responsedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  // FIXME: https://foresightdata.atlassian.net/browse/MAGGIE-5684 (check meta property on IKPITemplateSectionDataModel)
  // unknown because weird bug with react-hook-form and typescript
  sections: unknown[] | null | undefined;
  sectionData: IKPIResponseFormSectionData[];
  kpiData: IKPIResponseFormKPIData;
  kpiRequest: unknown | null | undefined;
}

export interface IKPIResponseActionPayload extends Pick<IKPIRequestResponse, 'sectionData' | 'kpiData'> {}

export interface IKPIRequestResponse {
  createdAt?: string;
  updatedAt?: string;
  id: number;
  sectionData: IKPIResponseFormSectionData[];
  kpiData: IKPIResponseRequestKPIData;
  respondedAt?: string | null;
  submittedBy?: string | null;
}

export function createKPIRequestResponseDataModel(
  overrides: Partial<IKPIRequestResponse> = {}
): IKPIRequestResponse {
  return {
    id: -1,
    kpiData: createKPIGridSectionDataModel(overrides.kpiData),
    sectionData: [],
    ...overrides,
  };
}

export function createKPIGridSectionDataModel(
  overrides: Partial<IKPIResponseRequestKPIData> = {}
): IKPIResponseRequestKPIData {
  return {
    period: KPIRequestPeriod.Month,
    currencyCode: 'USD',
    values: [],
    ...overrides,
  };
}

export function createKPIFormSectionDataModel(
  overrides: Partial<IKPIResponseFormSectionData> = {}
): IKPIResponseFormSectionData {
  return {
    sectionId: 0,
    value: null,
    ...overrides,
  };
}

export function periodFromGranularityAndFrequency(
  granularity: KPIRequestFrequency | null | undefined,
  frequency: KPIRequestFrequency = KPIRequestFrequency.Monthly
): KPIRequestPeriod {
  if (granularity === KPIRequestFrequency.None || !granularity) {
    return frequencyToPeriod[frequency];
  }
  if (granularity === KPIRequestFrequency.Annual && frequency === KPIRequestFrequency.Annual) {
    return KPIRequestPeriod.Annual;
  }

  return frequencyToPeriod[granularity];
}

export function createInitialResponseFromTemplate(
  request: IKPIRequestDataModel,
  template: IKPITemplate
): Partial<IKPIRequestResponse> {
  return createKPIRequestResponseDataModel({
    kpiData: {
      currencyCode: 'USD',
      period: periodFromGranularityAndFrequency(request.granularity, request.frequency),
      values: [],
    },
    sectionData:
      template.sections?.reduce((res, section) => {
        if (section.type !== 'table') {
          res.push(
            createKPIFormSectionDataModel({
              sectionId: section.id,
              value: null,
            })
          );
        }
        return res;
      }, [] as IKPIResponseFormSectionData[]) ?? [],
  });
}

export const kpiRequestStatusToConfigMap: Record<
  KPIRequestStatus | CompanyPeriodRequestStatus,
  { label: string; color: string }
> = {
  [KPIRequestStatus.NotSent]: { label: 'To Send', color: colors.primary[80] },
  [KPIRequestStatus.Sent]: { label: 'Sent', color: colors.primary[30] },
  [KPIRequestStatus.Submitted]: { label: 'Submitted', color: colors.secondary[60] },
  [KPIRequestStatus.Rejected]: { label: 'Rejected', color: colors.critical[60] },
  [KPIRequestStatus.Accepted]: { label: 'Accepted', color: colors.success[60] },
  [KPIRequestStatus.Archived]: { label: 'Archived', color: colors.neutral[30] },

  [KPIRequestStatus.ManagedServiceSubmitted]: { label: 'Pending', color: colors.tertiary[40] },
  [KPIRequestStatus.ManagedServiceInProgress]: { label: 'In Review', color: colors.tertiary[60] },
  [KPIRequestStatus.ManagedServiceFinished]: { label: 'MS Submitted', color: colors.tertiary[80] },
  [CompanyPeriodRequestStatus.draft]: { label: 'Draft', color: colors.primary[30] },
  [CompanyPeriodRequestStatus.processing]: { label: 'Processing', color: colors.tertiary[60] },
  [CompanyPeriodRequestStatus.responded]: { label: 'Responded', color: colors.secondary[60] },
};

export function kpiRequestStatusToColor(status: KPIRequestStatus | CompanyPeriodRequestStatus) {
  return kpiRequestStatusToConfigMap[status]?.color ?? 'rgba(182, 187, 196, 1)';
}
